import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * BomComparisonView — renders a `BomComparison` (contrato-sun.md §2) as three blocks: added, removed
 * and modified components. Shared by the library compare dialog (S52), the publish preview (S102) and
 * the update-impact dialog (HU-07). Purely presentational: the comparison is shown exactly as Sun sends it.
 *
 * PO 2026-09-10: each block is a small `SmartMateriaUITable` (dense, no search/pagination, every
 * row on one page) so the comparison keeps the same look as the rest of the module's tables.
 *
 * @param {object} props
 * @param {object} props.comparison BomComparison: { fromBomId, fromVersionNumber, toBomId, toVersionNumber, added[], removed[], modified[] }
 */
const BomComparisonView = ({ comparison }) => {
  const { t_ } = useSmartTranslation()
  if (!comparison) return null

  const added = comparison.added || []
  const removed = comparison.removed || []
  const modified = comparison.modified || []
  const isEmpty = added.length === 0 && removed.length === 0 && modified.length === 0
  // fromVersionNumber = 0 means the origin is the workflow's embedded BOM (publish preview, N22)
  const fromLabel = comparison.fromVersionNumber ? t_('Version') + ' ' + comparison.fromVersionNumber : t_('Workflow BOM')

  return (
    <Stack spacing={2} data-testid='bom-comparison'>
      <Typography variant='subtitle2' data-testid='bom-comparison-versions'>
        {fromLabel} → {t_('Version')} {comparison.toVersionNumber}
      </Typography>
      {isEmpty
        ? <Typography variant='body2'>{t_('No differences between the compared versions')}</Typography>
        : (
          <>
            <ComparisonItemsBlock testId='bom-comparison-added' title={t_('Added')} color='success' items={added} t_={t_} />
            <ComparisonItemsBlock testId='bom-comparison-removed' title={t_('Removed')} color='error' items={removed} t_={t_} />
            <ModifiedBlock items={modified} t_={t_} />
          </>
          )}
    </Stack>
  )
}

const FIELD_LABELS = {
  quantity: 'Quantity',
  revision: 'Revision',
  parent: 'Parent',
  category: 'Category',
  componentType: 'Component type'
}

const formatParentPath = (parentCodePath) => (parentCodePath || []).join(' > ')

const formatValue = (value) => (value === null || value === undefined ? '—' : String(value))

const readFieldValue = (item, field) => {
  if (!item) return null
  if (field === 'parent') return formatParentPath(item.parentCodePath)
  return item[field]
}

/** Props shared by the three blocks: a compact, read-only table showing every row at once. */
const compactTableProps = {
  dense: true,
  sticky: false,
  searchAndPaginationVisible: 'none',
  tablePageSize: Number.MAX_SAFE_INTEGER,
  primaryKeys: ['id'],
  sortOptions: { disableAutoSort: true }
}

const BlockTitle = ({ title, color, count }) => (
  <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
    <Typography variant='subtitle2'>{title}</Typography>
    <Chip size='small' color={color} label={count} />
  </Stack>
)

const ComparisonItemsBlock = ({ testId, title, color, items, t_ }) => {
  if (items.length === 0) return null
  // Sun's order is preserved (the table is told not to auto-sort); the id is only the row key.
  const rows = items.map((item, index) => ({ ...item, id: item.componentKey + '#' + index }))
  const columns = [
    { name: t_('Code'), field: 'resourceCode', readOnly: true },
    { name: t_('Name'), field: 'resourceName', readOnly: true, render: (row) => formatValue(row.resourceName) },
    { name: t_('Parent'), field: 'parentCodePath', readOnly: true, render: (row) => formatParentPath(row.parentCodePath) },
    { name: t_('position'), field: 'position', readOnly: true },
    { name: t_('Quantity'), field: 'quantity', numeric: true, readOnly: true, render: (row) => formatValue(row.quantity) },
    { name: t_('Revision'), field: 'revision', readOnly: true, render: (row) => formatValue(row.revision) }
  ]
  return (
    <Box data-testid={testId}>
      <BlockTitle title={title} color={color} count={items.length} />
      <SmartMateriaUITable {...compactTableProps} columns={columns} dataFetch={() => Promise.resolve(rows)} />
    </Box>
  )
}

const ModifiedBlock = ({ items, t_ }) => {
  if (items.length === 0) return null
  // One row per changed field of each modified component (before → after).
  const rows = items.flatMap((item, index) => (item.changedFields || []).map((field) => ({
    id: item.componentKey + '#' + index + '#' + field,
    resourceCode: item.resourceCode,
    parentCodePath: item.parentCodePath,
    position: item.position,
    field,
    before: readFieldValue(item.before, field),
    after: readFieldValue(item.after, field)
  })))
  const columns = [
    { name: t_('Code'), field: 'resourceCode', readOnly: true },
    { name: t_('Parent'), field: 'parentCodePath', readOnly: true, render: (row) => formatParentPath(row.parentCodePath) },
    { name: t_('position'), field: 'position', readOnly: true },
    { name: t_('Field'), field: 'field', readOnly: true, render: (row) => t_(FIELD_LABELS[row.field] || row.field) },
    { name: t_('Before'), field: 'before', readOnly: true, render: (row) => formatValue(row.before) },
    { name: t_('After'), field: 'after', readOnly: true, render: (row) => formatValue(row.after) }
  ]
  return (
    <Box data-testid='bom-comparison-modified'>
      <BlockTitle title={t_('Modified')} color='warning' count={items.length} />
      <SmartMateriaUITable {...compactTableProps} columns={columns} dataFetch={() => Promise.resolve(rows)} />
    </Box>
  )
}

export default BomComparisonView
